import React, { useEffect, useState } from "react";
import axios from "axios";
import { ApiUrl } from "Services/Services";

export default function FloorTableScreen() {
  const [branches, setBranches] = useState([]);
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [showFloorModal, setShowFloorModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);

  // Form states
  const [floorForm, setFloorForm] = useState({ branchId: "", name: "" });
  const [tableForm, setTableForm] = useState({
    floorId: "",
    number: "",
    seats: "",
    status: "available",
  });

  useEffect(() => {
    fetchBranches();
    fetchFloors();
  }, []);

  const fetchBranches = async () => {
    try {
      const data = await axios.put(`${ApiUrl}/branches`, {
        organizationId: localStorage.getItem("organizationId"),
      });
      setBranches(data.data.data);
    } catch (err) {
      console.error("Error fetching branches", err);
    }
  };

  const fetchFloors = async () => {
    setLoading(true);
    try {
      const data = await axios.get(`${ApiUrl}/floor`);
      setFloors(data.data.data);
    } catch (err) {
      console.error("Error fetching floors", err);
    }
    setLoading(false);
  };

  const addFloor = async () => {
    try {
      await axios.post(`${ApiUrl}/floor`, floorForm);
      setShowFloorModal(false);
      setFloorForm({ branchId: "", name: "" });
      fetchFloors();
    } catch (err) {
      console.error("Error adding floor", err);
    }
  };

  const addTable = async () => {
    try {
      await axios.post(`${ApiUrl}/table`, tableForm);
      setShowTableModal(false);
      setTableForm({
        floorId: "",
        number: "",
        seats: "",
        status: "available",
      });
      fetchFloors();
    } catch (err) {
      console.error("Error adding table", err);
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Floors & Tables</h1>
        <div className="space-x-3">
          <button
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow transition"
            onClick={() => setShowFloorModal(true)}
          >
            + Add Floor
          </button>
          <button
            className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl shadow transition"
            onClick={() => setShowTableModal(true)}
          >
            + Add Table
          </button>
        </div>
      </div>

      {/* Floors */}
      {loading ? (
        <p className="text-gray-500">Loading floors...</p>
      ) : floors.length === 0 ? (
        <div className="text-center py-10 text-gray-500 border rounded-xl bg-gray-50">
          No floors available. Add one to get started.
        </div>
      ) : (
        floors.map((floor) => (
          <div
            key={floor.id}
            className="mb-8 border rounded-xl shadow-sm bg-white"
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">
                {floor.name}
              </h2>
              <span className="text-sm text-gray-500">
                {floor.Tables?.length || 0} Tables
              </span>
            </div>

            {floor.Tables?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 p-4">
                {floor.Tables.map((table) => (
                  <div
                    key={table.id}
                    className="p-4 border rounded-lg bg-gray-50 hover:shadow transition"
                  >
                    <p className="font-medium text-gray-800 mb-1">
                      Table {table.number}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      Seats: {table.seats}
                    </p>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        table.status === "available"
                          ? "bg-green-100 text-green-700"
                          : table.status === "occupied"
                          ? "bg-red-100 text-red-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {table.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 text-gray-500">No tables in this floor.</div>
            )}
          </div>
        ))
      )}

      {/* Floor Modal */}
      {showFloorModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-96 animate-fadeIn">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">
              Add Floor
            </h2>

            <label className="block mb-2 text-sm font-medium text-gray-700">
              Branch
            </label>
            <select
              value={floorForm.branchId}
              onChange={(e) =>
                setFloorForm({ ...floorForm, branchId: e.target.value })
              }
              className="w-full border rounded-lg px-3 py-2 mb-4 focus:ring focus:ring-blue-200"
            >
              <option value="">Select Branch</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>

            <label className="block mb-2 text-sm font-medium text-gray-700">
              Floor Name
            </label>
            <input
              type="text"
              value={floorForm.name}
              onChange={(e) =>
                setFloorForm({ ...floorForm, name: e.target.value })
              }
              className="w-full border rounded-lg px-3 py-2 mb-4 focus:ring focus:ring-blue-200"
              placeholder="e.g. First Floor"
            />

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition"
                onClick={() => setShowFloorModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                onClick={addFloor}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Modal */}
      {showTableModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-96 animate-fadeIn">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">
              Add Table
            </h2>

            <label className="block mb-2 text-sm font-medium text-gray-700">
              Floor
            </label>
            <select
              value={tableForm.floorId}
              onChange={(e) =>
                setTableForm({ ...tableForm, floorId: e.target.value })
              }
              className="w-full border rounded-lg px-3 py-2 mb-4 focus:ring focus:ring-green-200"
            >
              <option value="">Select Floor</option>
              {floors.map((floor) => (
                <option key={floor.id} value={floor.id}>
                  {floor.name}
                </option>
              ))}
            </select>

            <label className="block mb-2 text-sm font-medium text-gray-700">
              Table Number
            </label>
            <input
              type="text"
              value={tableForm.number}
              onChange={(e) =>
                setTableForm({ ...tableForm, number: e.target.value })
              }
              className="w-full border rounded-lg px-3 py-2 mb-4 focus:ring focus:ring-green-200"
              placeholder="e.g. T1"
            />

            <label className="block mb-2 text-sm font-medium text-gray-700">
              Seats
            </label>
            <input
              type="number"
              value={tableForm.seats}
              onChange={(e) =>
                setTableForm({ ...tableForm, seats: e.target.value })
              }
              className="w-full border rounded-lg px-3 py-2 mb-4 focus:ring focus:ring-green-200"
              placeholder="e.g. 4"
            />

            <label className="block mb-2 text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              value={tableForm.status}
              onChange={(e) =>
                setTableForm({ ...tableForm, status: e.target.value })
              }
              className="w-full border rounded-lg px-3 py-2 mb-4 focus:ring focus:ring-green-200"
            >
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="reserved">Reserved</option>
            </select>

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition"
                onClick={() => setShowTableModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
                onClick={addTable}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
